import React, { useCallback, useContext, useState } from 'react';
import useMemoObject from './useMemoObject';

// InteractionContext is in charge of the state management across components. This way the select
// and the hover state can be kept in sync without a local state in the outermost chart component.

const InteractionContext = React.createContext<any>({});

const toggleSelected = (id: any) => (state: any) =>
  state.includes(id) ? state.filter((s: any) => s !== id) : [...state, id];

const selectOne = (id: any) => (state: any) => (state.length === 1 && state[0] === id ? [] : [id]);

export const InteractionContextProvider = ({ selectShift, selectToggle, children }: any) => {
  const [hoveredTick, setHoveredTick] = useState<any>(undefined);
  const [hovered, setHovered] = useState(undefined);
  const [selected, setSelected] = useState<string[]>([]);

  const isSelected = useCallback((id: any) => selected.includes(id), [selected]);
  const isHovered = useCallback((id: any) => id === hovered, [hovered]);

  // we might need to move this to chart context
  const handleHoverTick = useCallback(
    (value: any) => setHoveredTick((prev: any) => (prev - value === 0 ? prev : value)),
    [],
  );

  const handleMouseEnter = useCallback((id: any, event: any) => {
    event.stopPropagation();
    setHovered(prevId => (prevId !== id ? id : prevId));
  }, []);

  const handleMouseLeave = useCallback((id: any) => {
    setHovered(prevId => (prevId === id ? undefined : prevId));
  }, []);

  const handleClick = useCallback(
    (id: any, event: any) => {
      event.stopPropagation();
      const shiftKey = selectShift && event && event.shiftKey;
      const updateSelected = selectToggle || shiftKey ? toggleSelected : selectOne;
      setSelected(id ? updateSelected(id) : []);
    },
    [selectToggle, selectShift],
  );

  const value = useMemoObject({
    isHovered,
    isSelected,
    hoveredTick,
    handleHoverTick,
    handleMouseEnter,
    handleMouseLeave,
    handleClick,
  });

  return <InteractionContext.Provider value={value}>{children}</InteractionContext.Provider>;
};
export const useInteractionContext = () => useContext(InteractionContext); // variable
